/**
 * InvoiceQbParityBlock — the "QuickBooks invoice fields" block of the
 * invoice editor (PO / Work Order / Note to Customer / Memo on Statement /
 * labor QB Class), extracted from InvoiceEditorDialog.jsx (Jun 2026).
 * All state stays in the parent dialog and arrives as props.
 */
import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export const InvoiceQbParityBlock = ({
  poNumber,
  setPoNumber,
  workOrderNumber,
  setWorkOrderNumber,
  noteToCustomer,
  setNoteToCustomer,
  memoOnStatement,
  setMemoOnStatement,
  laborClassId,
  setLaborClassId,
  setLaborClassName,
  qbClasses,
}) => (
  <div
    className="rounded-lg border border-[#2D3748] bg-[#0B131F] p-3 space-y-3"
    data-testid="invoice-qb-parity-block"
  >
    <div className="text-xs uppercase tracking-wider text-slate-400">
      QuickBooks invoice fields
    </div>

    {/* Customer reference numbers (Feb 2026 — Review & Push). PO
        Number maps to QBO's native Invoice.PONumber. Work Order
        Number rides in PrivateNote when included in the push. */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <Label htmlFor="invoice-po-number" className="text-xs text-slate-300">
          PO Number
          <span className="text-[10px] text-slate-500 ml-1">(customer-visible on QBO PDF)</span>
        </Label>
        <Input
          id="invoice-po-number"
          value={poNumber}
          onChange={(e) => setPoNumber(e.target.value)}
          placeholder="e.g. PO-44512"
          maxLength={25}
          className="mt-1 bg-[#1A2433] border-[#2D3748] text-white text-sm h-9"
          data-testid="invoice-po-number-input"
        />
      </div>
      <div>
        <Label htmlFor="invoice-work-order" className="text-xs text-slate-300">
          Work Order #
          <span className="text-[10px] text-slate-500 ml-1">(internal — to QBO memo when selected)</span>
        </Label>
        <Input
          id="invoice-work-order"
          value={workOrderNumber}
          onChange={(e) => setWorkOrderNumber(e.target.value)}
          placeholder="e.g. WO-7788"
          maxLength={64}
          className="mt-1 bg-[#1A2433] border-[#2D3748] text-white text-sm h-9"
          data-testid="invoice-work-order-input"
        />
      </div>
    </div>

    <div>
      <Label htmlFor="invoice-note-customer" className="text-xs text-slate-300">
        Note to Customer
        <span className="text-[10px] text-slate-500 ml-1">(visible on PDF & QBO invoice)</span>
      </Label>
      <Textarea
        id="invoice-note-customer"
        value={noteToCustomer}
        onChange={(e) => setNoteToCustomer(e.target.value)}
        placeholder="Thank you for your business! Net 30 — please pay by e-transfer to billing@…"
        className="mt-1 bg-[#1A2433] border-[#2D3748] text-white text-sm min-h-[60px]"
        data-testid="invoice-note-customer-input"
      />
    </div>

    <div>
      <Label htmlFor="invoice-memo-statement" className="text-xs text-slate-300">
        Memo on Statement
        <span className="text-[10px] text-slate-500 ml-1">(hidden — internal only, QBO PrivateNote)</span>
      </Label>
      <Textarea
        id="invoice-memo-statement"
        value={memoOnStatement}
        onChange={(e) => setMemoOnStatement(e.target.value)}
        placeholder="Internal notes the customer never sees (PO ref, dispatch note, etc.)"
        className="mt-1 bg-[#1A2433] border-[#2D3748] text-white text-sm min-h-[50px]"
        data-testid="invoice-memo-statement-input"
      />
    </div>

    <div>
      <Label htmlFor="labor-class" className="text-xs text-slate-300">
        Labor line — QB Class
        <span className="text-[10px] text-slate-500 ml-1">
          {laborClassId
            ? '(overrides project default for this invoice)'
            : '(defaults to the project\u2019s class)'}
        </span>
      </Label>
      <Select
        value={laborClassId || 'none'}
        onValueChange={(v) => {
          if (v === 'none') {
            setLaborClassId('');
            setLaborClassName('');
            return;
          }
          const c = qbClasses.find((x) => x.qb_class_id === v);
          setLaborClassId(v);
          setLaborClassName(c ? (c.fully_qualified_name || c.name) : '');
        }}
      >
        <SelectTrigger
          id="labor-class"
          className="mt-1 bg-[#1A2433] border-[#2D3748] text-white text-sm"
          data-testid="invoice-labor-class-trigger"
        >
          <SelectValue placeholder={qbClasses.length ? 'Use project default' : 'No QB classes synced yet'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Use project default</SelectItem>
          {qbClasses.map((c) => (
            <SelectItem key={c.qb_class_id} value={c.qb_class_id}>
              {c.fully_qualified_name || c.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {!qbClasses.length && (
        <p className="text-[11px] text-slate-500 mt-1">
          Sync QB classes under Settings → Projects to populate this list.
        </p>
      )}
    </div>
  </div>
);

export default InvoiceQbParityBlock;
